import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { useHubToast } from "@/components/ui/HubToast";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import {
  fetchManufacturingPddCatch,
  fetchManufacturingPddCatchPreview,
  MANUFACTURING_PDD_CATCH_QUERY_KEY,
  manufacturingPddKindLabel,
  postManufacturingPddAssign,
  type ManufacturingPddZplCatchJob,
} from "./pddBatchItemLabelApi";

const MODAL_HELP =
  "ABB PDD carton and box files land in the GoDaddy catch folder after you print from ABB. Click a row to preview. Check one carton label and one box label, then assign. Pre-pack blocks ABB SKUs until both are assigned, then prints these instead of hub box and carton templates.";

type CatchSortKey = "kind" | "catalog" | "qty" | "receivedAt" | "file";

function jobDisplayQty(job: ManufacturingPddZplCatchJob): number {
  return job.kind === "carton" ? (job.labelQty ?? job.printCopies) : job.printCopies;
}

function compareCatchJobs(
  left: ManufacturingPddZplCatchJob,
  right: ManufacturingPddZplCatchJob,
  sortKey: CatchSortKey,
  dir: SortDirection,
): number {
  const direction = dir === "asc" ? 1 : -1;
  let compared = 0;
  if (sortKey === "receivedAt") {
    compared = Date.parse(left.receivedAt) - Date.parse(right.receivedAt);
  } else if (sortKey === "qty") {
    compared = jobDisplayQty(left) - jobDisplayQty(right);
  } else if (sortKey === "kind") {
    compared = manufacturingPddKindLabel(left.kind).localeCompare(manufacturingPddKindLabel(right.kind));
  } else if (sortKey === "catalog") {
    compared = (left.catalog ?? "").localeCompare(right.catalog ?? "");
    if (compared === 0) compared = (left.sku ?? "").localeCompare(right.sku ?? "");
  } else {
    compared = left.originalName.localeCompare(right.originalName);
  }
  if (compared !== 0) return compared * direction;
  return Date.parse(right.receivedAt) - Date.parse(left.receivedAt);
}

function formatCatchReceivedAt(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return iso;
  return new Date(parsed).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function AssignAbbPddLabelsModal({
  open,
  onClose,
  batchId,
  batchItemId,
  sku,
  quantity,
  alreadyAssigned,
}: {
  open: boolean;
  onClose: () => void;
  batchId: number;
  batchItemId: number;
  sku: string;
  quantity: number;
  alreadyAssigned: boolean;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const [cartonJobId, setCartonJobId] = useState<string | null>(null);
  const [boxJobId, setBoxJobId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<CatchSortKey>("receivedAt");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPreviewJobId(null);
      setCartonJobId(null);
      setBoxJobId(null);
      setMessage(null);
      setSortKey("receivedAt");
      setSortDir("desc");
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const catchQuery = useQuery({
    queryKey: MANUFACTURING_PDD_CATCH_QUERY_KEY,
    queryFn: fetchManufacturingPddCatch,
    enabled: open,
    refetchInterval: open ? 15_000 : false,
  });

  const jobs = catchQuery.data?.jobs ?? [];
  const sortedJobs = useMemo(
    () => [...jobs].sort((left, right) => compareCatchJobs(left, right, sortKey, sortDir)),
    [jobs, sortDir, sortKey],
  );
  const jobsById = useMemo(() => new Map(jobs.map((job) => [job.id, job])), [jobs]);
  const selectedCarton = cartonJobId != null ? (jobsById.get(cartonJobId) ?? null) : null;
  const selectedBox = boxJobId != null ? (jobsById.get(boxJobId) ?? null) : null;
  const canAssign =
    selectedCarton?.kind === "carton" && selectedBox?.kind === "unit";
  const previewJob = previewJobId != null ? (jobsById.get(previewJobId) ?? null) : null;

  const previewQuery = useQuery({
    queryKey: [...MANUFACTURING_PDD_CATCH_QUERY_KEY, "preview", previewJobId],
    queryFn: () => fetchManufacturingPddCatchPreview(previewJobId ?? ""),
    enabled: open && previewJobId != null,
    staleTime: 10 * 60_000,
  });

  const assignMutation = useMutation({
    mutationFn: async () => {
      if (selectedBox == null || selectedCarton == null) {
        throw new Error("Check one carton label and one box label.");
      }
      return postManufacturingPddAssign({
        batchId,
        batchItemId,
        unitId: selectedBox.id,
        cartonId: selectedCarton.id,
      });
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "batch-details"] });
      if (data.qtyWarning) {
        showToast({ message: data.qtyWarning, tone: "warning" });
      } else {
        showToast({
          message: "Assigned ABB box and carton labels to this line.",
          tone: "success",
        });
      }
      onClose();
    },
    onError: (error: unknown) => {
      setMessage(error instanceof ApiError || error instanceof Error ? error.message : "Assign failed.");
    },
  });

  function toggleSort(nextKey: CatchSortKey): void {
    if (nextKey === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === "receivedAt" ? "desc" : "asc");
  }

  if (!open) return null;

  const emptyReason = !catchQuery.data?.available
    ? "The PDD catch folder is not available on this hub host."
    : "No PDD labels are in the catch folder. Print them from ABB first, then return here.";

  return createPortal(
    <div
      {...hubModalOverlayProps(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-abb-pdd-labels-title"
    >
      <div className="my-4 flex w-full max-w-7xl flex-col rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="assign-abb-pdd-labels-title" className="text-lg font-semibold text-slate-900">
              {alreadyAssigned ? "Replace ABB labels" : "Assign ABB labels"}
            </h2>
            <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
          </div>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-5">
          <p className="font-mono text-sm text-slate-800">
            Batch {String(batchId)} line {String(batchItemId)} · {sku} · qty {String(quantity)}
          </p>

          {catchQuery.isLoading ? <p className="text-sm text-slate-500">Loading catch folder…</p> : null}
          {catchQuery.isError ? (
            <p className="text-sm text-amber-800">
              {catchQuery.error instanceof Error ? catchQuery.error.message : "Could not load PDD catch jobs."}
            </p>
          ) : null}
          {message ? (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">{message}</p>
          ) : null}

          <div className="flex min-h-0 flex-col gap-4 lg:flex-row">
            <div className="min-w-0 flex-1">
              <div className="max-h-[min(36rem,70vh)] overflow-auto rounded-md border border-slate-200">
                <table className="w-full min-w-[48rem] border-collapse text-sm">
                  <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
                    <tr>
                      <th scope="col" className="w-12 bg-slate-50 px-2 py-2 text-center text-xs font-medium uppercase tracking-wide text-slate-600">
                        Carton
                      </th>
                      <th scope="col" className="w-12 bg-slate-50 px-2 py-2 text-center text-xs font-medium uppercase tracking-wide text-slate-600">
                        Box
                      </th>
                      <SortableTableHeader
                        label="Kind"
                        sortKey="kind"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortableTableHeader
                        label="Catalog"
                        sortKey="catalog"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortableTableHeader
                        label="Qty"
                        sortKey="qty"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align="right"
                      />
                      <SortableTableHeader
                        label="Received"
                        sortKey="receivedAt"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortableTableHeader
                        label="File"
                        sortKey="file"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedJobs.length === 0 && !catchQuery.isLoading ? (
                      <tr>
                        <td colSpan={7} className="px-2 py-6 text-center text-slate-500">
                          {emptyReason}
                        </td>
                      </tr>
                    ) : (
                      sortedJobs.map((job) => (
                        <tr
                          key={job.id}
                          className={cn(
                            "cursor-pointer border-b border-slate-100 hover:bg-slate-50",
                            previewJobId === job.id ? "bg-sky-50 hover:bg-sky-50" : null,
                          )}
                          onClick={() => {
                            setMessage(null);
                            setPreviewJobId(job.id);
                          }}
                        >
                          <td className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              aria-label={`Assign carton label ${job.originalName}`}
                              className="h-4 w-4"
                              disabled={job.kind !== "carton"}
                              checked={cartonJobId === job.id}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => {
                                setMessage(null);
                                setPreviewJobId(job.id);
                                setCartonJobId(event.target.checked ? job.id : null);
                              }}
                            />
                          </td>
                          <td className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              aria-label={`Assign box label ${job.originalName}`}
                              className="h-4 w-4"
                              disabled={job.kind !== "unit"}
                              checked={boxJobId === job.id}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => {
                                setMessage(null);
                                setPreviewJobId(job.id);
                                setBoxJobId(event.target.checked ? job.id : null);
                              }}
                            />
                          </td>
                          <td className="px-2 py-2 text-slate-900">{manufacturingPddKindLabel(job.kind)}</td>
                          <td className="px-2 py-2 text-slate-700">
                            <div>{job.catalog ?? "—"}</div>
                            {job.sku ? <div className="text-xs text-slate-500">{job.sku}</div> : null}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums text-slate-900">
                            {jobDisplayQty(job)}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-slate-700">
                            {formatCatchReceivedAt(job.receivedAt)}
                          </td>
                          <td className="px-2 py-2 text-xs text-slate-500">{job.originalName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <aside className="w-full shrink-0 lg:w-96">
              <figure className="space-y-2">
                <div className="flex h-[min(36rem,70vh)] items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                  {previewJob == null ? (
                    <p className="px-3 text-center text-xs text-slate-500">Click a row to preview.</p>
                  ) : previewQuery.isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" aria-hidden />
                  ) : previewQuery.isError ? (
                    <p className="px-2 text-center text-xs text-amber-800">
                      {previewQuery.error instanceof Error ? previewQuery.error.message : "Preview failed."}
                    </p>
                  ) : previewQuery.data?.pngBase64 ? (
                    <img
                      src={`data:image/png;base64,${previewQuery.data.pngBase64}`}
                      alt={`${manufacturingPddKindLabel(previewJob.kind)} label preview`}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <p className="text-xs text-slate-500">No preview.</p>
                  )}
                </div>
                <figcaption className="min-h-[4.5rem] text-xs text-slate-600">
                  {previewJob == null ? (
                    <p className="text-slate-500">Preview stays here while you pick carton and box labels.</p>
                  ) : (
                    <>
                      <div className="font-medium text-slate-900">{manufacturingPddKindLabel(previewJob.kind)}</div>
                      <div>{previewJob.catalog ?? previewJob.originalName}</div>
                      {previewJob.sku ? <div className="text-slate-500">{previewJob.sku}</div> : null}
                      <div className="text-slate-500">{formatCatchReceivedAt(previewJob.receivedAt)}</div>
                    </>
                  )}
                </figcaption>
              </figure>
            </aside>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={assignMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canAssign}
            isLoading={assignMutation.isPending}
            onClick={() => {
              setMessage(null);
              assignMutation.mutate();
            }}
          >
            {alreadyAssigned ? "Replace assignment" : "Assign to this line"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
